export const REPORT_TYPES: string[] = ["Time entries", "Year report"];
export enum ReportType {
    TIME_ENTRIES = "Time entries",
    YEAR_REPORT = "Year report",
}

export interface User {
    id: string;
    app_settings: IAppSettings;
    full_name: string;
    email: string;
    oid: string;
    disabled: boolean;
    is_admin: boolean;
    is_project_manager: boolean;
    status_updated: number;
}

export interface IAppSettings {
    type: object;
    visible_projects: string[]; //list of project ids
    theme: string;
    // Backend flag: remember whether to show only projects with hours in the week view
    show_only_projects_with_hours?: boolean;
}

export interface IProject {
    active: {
        from: number | null;
        to: number | null;
    };
    admins: string[];
    company_id: string;
    entry_locked: boolean;
    id?: string;
    members: string[];
    owner_oid: string;
    private: boolean;
    project_name: string;
    tags: string[];
    budget: {
        used: number | null;
        total: number | null;
    };
    /** Optional free-form project note/description */
    note?: string | null;
}

export interface IUser {
    id?: string; // Database id
    app_settings: IAppSettings;
    full_name: string;
    email: string;
    oid: string;
    disabled: boolean;
    is_admin: boolean;
    is_project_manager: boolean;
    status_updated: number;
}

export interface ITimeEntry {
    id?: string; // Database id
    approved: boolean; // Is the time entry approved?
    created_by: string; // Created by user id
    created: number; // Unix timestamp
    modified: number; // Unix timestamp
    description: string; // Time entry description
    from: number; // Unix timestamp
    to: number; // Unix timestamp
    project_id: string; // Project id
}

export interface IProjectResults extends IProject {
    company_name: string;
}

export interface IWeekData {
    timeEntries: ITimeEntry[];
    weekTotal: { [key: string]: number };
    sum: { [key: string]: number };
    dates: { [key: string]: string };
}

/** Per-project logged hours for a calendar month + year (current user) */
export interface IProjectHourTotals {
    year: number;
    month: number; // 1–12
    totals: {
        [projectId: string]: {
            monthHours: number;
            yearHours: number;
        };
    };
}

export interface ICompanies {
    company_name: string;
    id: string;
    locked: boolean;
}

/** Default hours configuration for a user */
export interface IDefaultHoursAssignment {
    id?: string; // Database id
    user_oid: string; // User oid the configuration applies to
    project_id: string; // Project id to assign default hours to
    default_hours: number; // Default hours per day (e.g., 7.5)
    date_range: {
        from: number | null; // Unix timestamp - start date (null = no start limit)
        to: number | null; // Unix timestamp - end date (null = no end limit)
    };
    created?: number; // Unix timestamp (set by backend)
    modified?: number; // Unix timestamp (set by backend)
    created_by?: string; // Admin oid who created the configuration
}

/** Default hours configuration response */
export interface IDefaultHoursConfig {
    assignments: IDefaultHoursAssignment[];
    default_project_id: string; // The project ID used for default hours (e.g., "Default Hours" project)
}

/** Active default-hours configuration for a specific date (current user) */
export interface IDefaultHoursConfiguration {
    user_oid: string;
    project_id: string;
    default_hours: number;
    date_range: {
        from: number | null;
        to: number | null;
    };
    created: number;
    modified: number;
    created_by: string;
}

/** Default hours day info (per user + date) */
export interface IDefaultHoursDay {
    user_oid: string;
    date: number; // Start-of-day Unix timestamp (UTC-normalized)
    project_id: string; // Default hours project id
    mode: "auto" | "manual" | null;
    total_default_hours: number; // Effective total for the day (either config default or override)
    default_hours_available: number; // Remaining default hours (what the default project will show)
    overridden_default_hours: number | null; // When set, this is the override for total_default_hours
    created: number;
    modified: number;
}

/** Default hours consumption info for a specific day */
export interface IDefaultHoursConsumption {
    date: number; // Unix timestamp for the day
    total_default_hours: number; // Total default hours allocated (e.g., 7.5)
    consumed_hours: number; // Hours consumed from default hours
    remaining_hours: number; // Remaining default hours
    project_id: string; // Project ID where default hours are assigned
}
